import { accountIdParam, MIDDLEWARE_ERROR_CODES, RECAPTCHA_ERROR_CODES, RegistrationMode } from '@ytl/common-model';
import { useQueryParams } from '@ytl/common-web';
import axios from 'axios';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useApp } from '../../../contexts/appContext';
import { useLanguage } from '../../../contexts/languageContext';
import { RegistrationPhase } from '../../../pages/RegisterPage';
import { AppRoutes } from '../../../routes';
import { accountResource } from '../../../shared/resource/account.resource';
import { authResource } from '../../../shared/resource/auth.resource';
import { isValidEmail } from '../../../shared/utils/emailValidator';
import { parseJWT } from '../../../shared/utils/jwtUtils';
import { usePassword } from '../../../shared/utils/usePassword';

type AccountResponseTokenPayload = {
  email: string;
};

export const useRegister = (registrationMode: RegistrationMode) => {
  const { translation } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [view, setView] = useState(RegistrationPhase.AcceptTerms);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailAlreadyUsed, setEmailAlreadyUsed] = useState<boolean>(false);
  const [needsRedirect, setNeedsRedirect] = useState(false);

  const _usePassword = usePassword();
  const { password, validatePasswords } = _usePassword;
  const { setRegisterAccountId } = useApp();
  const location = useLocation();
  const query = useQueryParams();
  const [searchParams] = useSearchParams();
  const [emailJwt, setEmailJwt] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (location.pathname === AppRoutes.yoloRegister) {
      setIsLoading(true);
      // debugger;
      const search = new URLSearchParams(location.search);
      if (search.has(accountIdParam)) {
        setIsLoading(false);
        setRegisterAccountId(search.get(accountIdParam)!);
        setView(RegistrationPhase.EmailOTP);
      } else {
        accountResource
          .getYoloRegAuthUrl()
          .then(data => {
            setNeedsRedirect(true);
            window.location.href = data;
          })
          .catch(_err => {
            setIsLoading(false);
            setView(RegistrationPhase.RegistrationFailed);
          });
      }
    }
  }, [location, setRegisterAccountId]);

  useEffect(() => {
    if (location.pathname === AppRoutes.registrationCompleted) {
      if (query.get('completed') === 'true') {
        authResource.getAccount(false).catch(err => {
          console.warn('Failed to fetch account data after registration.', err);
        });
        setView(RegistrationPhase.RegistrationComplete);
      } else {
        setView(RegistrationPhase.RegistrationFailed);
      }
    }
  }, [location, query]);

  const register = useCallback(
    async (captchaToken: string, captchaReset: () => void) => {
      let validationFailure = false;
      if (!validatePasswords()) {
        validationFailure = true;
      }
      if (!isValidEmail(email)) {
        setEmailError(true);
        validationFailure = true;
      } else {
        setEmailError(false);
      }
      if (validationFailure) {
        setRegisterError('');
        setEmailAlreadyUsed(false);
        return;
      }

      try {
        setIsLoading(true);
        setRegisterError('');
        setEmailAlreadyUsed(false);

        const nameToStore = !!name ? name : email;
        const accountId = await accountResource.registerAccount({
          mode: registrationMode,
          name: nameToStore,
          email,
          password,
          ct: captchaToken,
        });
        captchaReset();
        if (accountId) {
          setRegisterAccountId(accountId);
        } else {
          setRegisterError(translation.loginRegister.register.box.error);
        }
        if (registrationMode === RegistrationMode.Yolo) {
          setView(RegistrationPhase.EmailOTP);
        } else {
          setView(RegistrationPhase.Result);
        }
      } catch (err) {
        captchaReset();

        if (axios.isAxiosError(err)) {
          const responseData = (err.response?.data as { message: string }).message;
          if (responseData === MIDDLEWARE_ERROR_CODES.EMAIL_ADDRESS_NOT_AVAILABLE) {
            setEmailAlreadyUsed(true);
          } else if (responseData === MIDDLEWARE_ERROR_CODES.INVALID_EMAIL_FORMAT) {
            setEmailError(true);
          } else if (
            responseData === RECAPTCHA_ERROR_CODES.TOKEN_INVALID ||
            responseData === RECAPTCHA_ERROR_CODES.TOKEN_MISSING
          ) {
            setRegisterError(translation.loginRegister.register.box.error);
          } else {
            setRegisterError(translation.loginRegister.register.box.error);
          }
        } else {
          setRegisterError(translation.loginRegister.register.box.error);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [email, name, password, registrationMode, translation, setRegisterAccountId, validatePasswords],
  );

  const finalizeRegistrationB2b = useCallback(async () => {
    if (!isValidEmail(email)) {
      setEmailError(true);
      setRegisterError('');
      setEmailAlreadyUsed(false);
      return;
    } else {
      setEmailError(false);
    }

    try {
      setIsLoading(true);
      setRegisterError('');
      setEmailAlreadyUsed(false);

      const accountResponse = await accountResource.finalizeRegistrationB2b(email);
      setEmailJwt(accountResponse);
      const accountResponsePayload = parseJWT<AccountResponseTokenPayload>(accountResponse);

      if (accountResponsePayload?.email) {
        setView(RegistrationPhase.Result);
      } else {
        setRegisterError(translation.loginRegister.register.box.error);
      }
    } catch (err) {
      setRegisterError(translation.loginRegister.register.box.errorMissingWFIN);
    } finally {
      setIsLoading(false);
    }
  }, [email, translation]);

  const validateOtp = useCallback(
    async (otp: string) => {
      await accountResource.validateOtp({
        email,
        otp,
      });
    },
    [email],
  );

  const completeRegistrationB2b = useCallback(
    async (otp: string) => {
      let validationFailure = false;
      if (!validatePasswords()) {
        validationFailure = true;
      }
      if (validationFailure) {
        setRegisterError('');
        setEmailAlreadyUsed(false);
        return;
      }

      await accountResource.completeRegistrationB2b({
        email,
        otp,
        password,
      });

      setView(RegistrationPhase.MFA);
    },
    [email, password, validatePasswords],
  );

  const updateEmail = useCallback((email: string) => {
    setEmail(email);
    setEmailError(false);
    setEmailAlreadyUsed(false);
  }, []);

  const yoloResend = async (registerAccountId: string | undefined) => {
    const hasAccountId = async (accountId: string) => {
      try {
        setIsLoading(true);
        await accountResource.resendOtp(accountId);
      } finally {
        setIsLoading(false);
      }
    };

    if (location.pathname.startsWith(AppRoutes.yoloRegister) && searchParams.has(accountIdParam)) {
      await hasAccountId(searchParams.get(accountIdParam)!);
    } else if (registerAccountId) {
      await hasAccountId(registerAccountId);
    } else {
      setView(RegistrationPhase.Form);
    }
  };
  const messageWithAction = useCallback(
    (
      msg: string,
      predicate: () => boolean,
      action: (s: string) => void,
      messageFormatter: (s: string) => string | React.JSX.Element = (s: string) => s,
    ) => {
      if (predicate()) {
        action(msg);
        return [messageFormatter(msg)];
      } else {
        return [];
      }
    },
    [],
  );
  return {
    isLoading,
    setIsLoading,
    registerError,
    view,
    setView,
    name,
    setName,
    password,
    email,
    setEmail: updateEmail,
    emailError,
    emailAlreadyUsed,
    register,
    finalizeRegistrationB2b,
    validateOtp,
    completeRegistrationB2b,
    _usePassword,
    needsRedirect,
    yoloResend,
    setNeedsRedirect,
    messageWithAction,
    emailJwt,
  };
};
